import { Switch, Case } from 'react-if';
import {
  EmailTemplateTypes,
  PolicyCalculationType,
  PolicyDayPeriodType,
  PolicyTargetStatus,
} from 'shared';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { RadioStack } from '../../../../../common/Atoms/RadioStack';
import { InputTextAddon } from '../../../../../common/Atoms/InputTextAddon';
import { fieldIsValid } from '../../../../lib/helper';
import { useAccountingSystemTerm } from '../../../../hooks/useAccountingSystemTerm';
import { Grid } from '../../../../../common/Atoms/Grid';
import { GridItem } from '../../../../../common/Atoms/GridItem';
import { CurrencyInput } from '../../../../../common/Atoms/CurrencyInput';
import { AccountSelector } from '../PolicyForm/AccountSelector';
import { IPolicyProps } from '../PolicyItem';
import { TaxSelector } from '../PolicyForm/TaxSelector';
import { EmailTemplateSelector } from '../PolicyForm/Fields/EmailTemplateSelector';
import { DayIndexWithType } from '../PolicyForm/Fields/DayIndexWithType';
import { Select } from '../../../../../common/Atoms/Select';
import { FormLabel } from '../../../../../common/Atoms/FormLabel';
import { Divider } from '../../../../../common/Atoms/Divider';
import { Transition } from '../../../../../common/Atoms/Transition';
import { SenderProfileSelector } from '../PolicyForm/SenderProfileSelector';
import { SkipWhenPromisedOrQueryOpen } from '../PolicyForm/Fields/SkipWhenPromisedOrQueryOpen';
import { useAccountingSystemName } from '../../../../hooks/useAccountingSystemName';

const calcucateOptions = [
  {
    title: `Fixed Discount`,
    description: `Applies a fixed discount in the invoice's base currency`,
    value: PolicyCalculationType.FIXED,
  },
  {
    title: `Percentage Discount`,
    description: `Applies a discount based on the percentage of the invoice total`,
    value: PolicyCalculationType.PERCENT,
  },
];

const discountAppliesOptions = [
  {
    title: `Apply to new invoices`,
    description: `Apply discounts to any new invoice`,
    value: `new`,
  },
  {
    title: `Apply only to unsent invoices`,
    description: `Apply discounts to unsent new invoices`,
    value: `unsent`,
  },
];

function calculateDayTime(value: number, inverse?: boolean): `before` | `after` | `on` {
  if (value === 0) {
    return `on`;
  }

  if (inverse) {
    if (value < 0) {
      return `after`;
    }

    return `before`;
  }

  if (value < 0) {
    return `before`;
  }

  return `after`;
}

export function PolicyPromptPaymentDiscount({ policy, invalids, handleChange }: IPolicyProps) {
  const accountingSystemName = useAccountingSystemName();
  const [dayType, setDayType] = useState(calculateDayTime(policy.day_index));
  const [removalDayType, setRemovalDayType] = useState(calculateDayTime(policy.grace_period, true));

  useEffect(() => {
    setDayType(calculateDayTime(policy.day_index));
  }, [ policy.day_index ]);

  useEffect(() => {
    setRemovalDayType(calculateDayTime(policy.grace_period, true));
  }, [ policy.grace_period ]);

  const dayTypes = useMemo(() => {
    const result =[
      {
        label: `Before`,
        value: `before`,
      },
    ];

    if (policy.target_status === PolicyTargetStatus.ISSUED) {
      result.unshift({
        label: `After`,
        value: `after`,
      });

      result.unshift({
        label: `On the`,
        value: `on`,
      });
    }

    return result;
  }, [ policy.target_status ]);

  const removalDayTypes = useMemo(() => {
    if (policy.discount_removal_target_status === PolicyTargetStatus.DUE) {
      return [
        {
          label: `After`,
          value: `after`,
        },
        {
          label: `On The`,
          value: `on`,
        },
        {
          label: `Before`,
          value: `before`,
        },
      ];
    }

    if (policy.discount_removal_target_status === PolicyTargetStatus.ISSUED) {
      return [
        {
          label: `After`,
          value: `after`,
        },
      ];
    }

    // Shouldnt be here
    return [];
  }, [ policy.discount_removal_target_status ]);

  const invoiceStates = [
    {
      label: `Due Date`,
      value: PolicyTargetStatus.DUE,
    },
    {
      label: `Issue Date`,
      value: PolicyTargetStatus.ISSUED,
      tooltip: `Tip: Use this to be able to schedule a discount ahead of time or after an approval period`,
    },
  ];

  const dayIndexValue = useMemo(() => {
    let str = policy?.day_index?.toString() || ``;

    if (str.startsWith(`-`)) {
      str = str.replace(`-`, ``);
    }

    return str;
  }, [ policy.day_index ]);

  const dayRemovalIndexValue = useMemo(() => {
    let str = policy?.grace_period?.toString() || ``;

    if (str.startsWith(`-`)) {
      str = str.replace(`-`, ``);
    }

    return str;
  }, [ policy.grace_period ]);

  function onDayIndexChange(e, day = dayType) {
    const { value } = e;

    let num = Number(value);

    if (Number.isNaN(num)) {
      handleChange({
        name: `day_index`,
        value,
      });

      return;
    }

    if (num !== 0) {
      if (day === `before`) {
        num = num < 0 ? num : -num;
      }

      if (day === `after`) {
        num = num > 0 ? num : -num;
      }

      if (day === `on`) {
        num = 0;
      }
    }

    handleChange({
      name: `day_index`,
      value: num,
    });
  }

  function onDayRemovalChange(e, day = removalDayType) {
    const { value } = e;

    let num = Number(value);

    if (Number.isNaN(num)) {
      handleChange({
        name: `grace_period`,
        value,
      });

      return;
    }

    if (num !== 0) {
      // Inverse logic for removal
      if (day === `after`) {
        num = num < 0 ? num : -num;
      }

      if (day === `before`) {
        num = num > 0 ? num : -num;
      }

      if (day === `on`) {
        num = 0;
      }
    }

    handleChange({
      name: `grace_period`,
      value: num,
    });
  }

  function handleDayTypeChange(e) {
    setDayType(e.value);

    onDayIndexChange({
      value: policy.day_index,
    }, e.value);

    if (e.value === `on` && policy.day_period_type === PolicyDayPeriodType.WEEKDAYS) {
      // Set weekdays to days if they select `on`
      handleChange({
        name: `day_period_type`,
        value: PolicyDayPeriodType.DAYS,
      });
    }
  }

  function handleRemovalDayTypeChange(e) {
    setRemovalDayType(e.value);

    onDayRemovalChange({
      value: policy.grace_period,
    }, e.value);
  }

  function handleTargetStatusChange(e) {
    handleChange(e);
  }

  return (
    <div>
      <RadioStack
        options={ calcucateOptions }
        onChange={ handleChange }
        selected={ policy.calculation_type }
        name={ `calculation_type` }
      />

      <RadioStack
        options={ discountAppliesOptions }
        onChange={ handleChange }
        selected={ policy.discount_applies }
        name={ `discount_applies` }
      />

      <Grid
        cols={ 3 }
        gapX={ 2 }
      >
        <GridItem>
          <AccountSelector
            label={ useAccountingSystemTerm(`Discount Account`) }
            value={ policy.xero_account_code }
            valueKey={ `code` }
            onChange={ handleChange }
            name={ `xero_account_code` }
            invalid={ fieldIsValid(`xero_account_code`, invalids) }
            description={ `The ${accountingSystemName} account to record the negative amount against` }
          />
        </GridItem>
        <GridItem
        >
          <TaxSelector
            label={ useAccountingSystemTerm(`Tax Rate`) }
            code={ policy.tax_rate }
            onChange={ handleChange }
            name={ `tax_rate` }
            invalid={ fieldIsValid(`tax_rate`, invalids) }
          />
        </GridItem>
        <GridItem>
          <Switch>
            <Case condition={ policy.calculation_type === PolicyCalculationType.FIXED }>
              <CurrencyInput
                label={ `Discount Amount` }
                invalid={ fieldIsValid(`fixed_fee_amount`, invalids) }
                value={ policy.fixed_fee_amount }
                onChange={ handleChange }
                name={ `fixed_fee_amount` }
                description={ `The amount to discount the invoice by. In the Invoice's base currency` }
              />
            </Case>
            <Case condition={ policy.calculation_type === PolicyCalculationType.PERCENT || policy.calculation_type === PolicyCalculationType.PRORATA }>
              <InputTextAddon
                invalid={ fieldIsValid(`percent`, invalids) }
                label={ `Discount %` }
                value={ policy.percent }
                onChange={ handleChange }
                addOnText={ `%` }
                name={ `percent` }
                description={ `The percentage to discount the invoice by` }
              />
            </Case>
          </Switch>
        </GridItem>
      </Grid>

      <Divider
        weight={ `light` }
        spacing={ `xs` }
      />

      <div>
        <FormLabel>{ `Apply this discount up to:` }</FormLabel>
        <Grid cols={ 3 }
          className={ `items-end` }>
          <GridItem>
            <DayIndexWithType
              handleChange={ handleChange }
              policyFormData={ policy }
              invalids={ invalids }
              dayType={ dayType }
              dayIndexValue={ dayIndexValue }
              onDayIndexChange={ onDayIndexChange }
              allowOrLater={ false }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ dayTypes }
              selected={ dayType }
              onChange={ handleDayTypeChange }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ invoiceStates }
              selected={ policy.target_status }
              onChange={ handleTargetStatusChange }
              name={ `target_status` }
              invalid={ fieldIsValid(`target_status`, invalids) }
            />
          </GridItem>
        </Grid>
      </div>

      <div>
        <FormLabel>{ `Remove this discount on:` }</FormLabel>
        <Grid cols={ 3 }
          className={ `items-end` }>
          <GridItem>
            <InputTextAddon
              value={ dayRemovalIndexValue }
              onChange={ onDayRemovalChange }
              dataType={ `number` }
              addOnText={ `days` }
              name={ `grace_period` }
              invalid={ fieldIsValid(`grace_period`, invalids) }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ removalDayTypes }
              selected={ removalDayType }
              onChange={ handleRemovalDayTypeChange }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ invoiceStates }
              selected={ policy.discount_removal_target_status }
              onChange={ handleTargetStatusChange }
              name={ `discount_removal_target_status` }
              invalid={ fieldIsValid(`discount_removal_target_status`, invalids) }
            />
          </GridItem>
        </Grid>
      </div>

      <InputTextAddon
        label={ `Description` }
        value={ policy.line_item_text }
        onChange={ handleChange }
        name={ `line_item_text` }
        invalid={ fieldIsValid(`line_item_text`, invalids) }
        description={ `Appears on the invoice. Use #DiscountDate for the date the discount will be removed` } />

      <EmailTemplateSelector
        value={ policy.template_id }
        type={ EmailTemplateTypes.DISCOUNT }
        handleChange={ handleChange }
        invalid={ fieldIsValid(`template_id`, invalids) }
        name={ `template_id` }
        label={ `Send email notification to customer when discount is applied` }
        nullable
        emptyText={ `Do not send email notification` }
        helpIcon={ `Create or edit the template under Email Templates.` }
        currentPolicyId={ policy.id }

      />

      <Transition
        speed={ `slow` }
        show={ !!policy.template_id }
      >
        <span>
          <div className={ `mt-2` }>
            <SenderProfileSelector
              handleChange={ handleChange }
              policyFormData={ policy }
              invalids={ invalids }
              anchor={ `top` }
            />
          </div>
        </span>
      </Transition>
      <SkipWhenPromisedOrQueryOpen
        handleChange={ handleChange }
        policyFormData={ policy }
        invalids={ invalids }
      />
    </div>
  );
}
